import { useCallback } from "react";
import { Button } from "react-bulma-components";
import { useTranslation } from "react-i18next";
import { logUpsellSurfaceClicked } from "../../models/analytics";
import { useUpsellImpression } from "../../util/useUpsellImpression";
import { useFlags } from "../flags/FeatureFlagProvider";
import { pricingRouteHighlight, useRouteContext } from "../route/RouteProvider";
import { CompositeAvatar } from "./CompositeAvatar";
import { useAvatarConfig } from "./avatarConfig";
import { CELL_SIZE, RENDER_SCALE } from "./spriteData";
import type { AvatarConfig, AvatarHeight, WeaponType } from "./types";

const HEIGHTS: AvatarHeight[] = ["tall", "medium", "short"];
const WEAPONS: (WeaponType)[] = [null, "staff", "sword"];
const HAT_OPTIONS: (number | null)[] = [0, 1, 2, 3, 4, null];
const HAT_LABELS: Record<string, string> = {
  "0": "Farmer",
  "1": "Flower",
  "2": "Mohawk",
  "3": "Greaser",
  "4": "Crown",
};

function cycle<T>(arr: T[], current: T, dir: 1 | -1): T {
  const idx = arr.indexOf(current);
  return arr[(idx + dir + arr.length) % arr.length];
}

function heightLabel(h: AvatarHeight): string {
  return h.charAt(0).toUpperCase() + h.slice(1);
}

const WEAPON_LABELS: Record<string, string> = {
  staff: "Staff",
  sword: "Sword",
};

function weaponLabel(w: WeaponType): string {
  if (w === null) return "None";
  return WEAPON_LABELS[w] ?? w;
}

export function AvatarConfigPreview({ onNavigateAway }: { onNavigateAway?: () => void } = {}) {
  const { t } = useTranslation();
  const [config, setConfig] = useAvatarConfig();
  const { isEnabled } = useFlags();
  const [, setRoute] = useRouteContext();
  const launchV1 = isEnabled("launchV1");
  const gearEnabled = isEnabled("avatarGear");
  const upsellVisible = launchV1 && !gearEnabled;
  useUpsellImpression("avatar_gear", "supporter", upsellVisible);

  const update = useCallback((patch: Partial<AvatarConfig>) => {
    setConfig((prev: AvatarConfig) => {
      const next = { ...prev, ...patch };
      if (next.height === "short") next.hasShield = false;
      return next;
    });
  }, [setConfig]);

  const previewScale = RENDER_SCALE * 2;
  const cellPx = CELL_SIZE * previewScale;
  // Extra height for tall hats (up to 6 sprite pixels above the cell)
  const hatOverflow = 6 * previewScale;
  const previewSize = cellPx + hatOverflow;

  return (
    <div>
      <p className="subtitle" style={{ marginBottom: "0.75rem" }}>Avatar</p>
      <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", height: previewSize }}>
        {/* Preview with dark rounded background */}
        <div
          style={{
            position: "relative",
            width: previewSize,
            height: previewSize,
            flexShrink: 0,
            background: "#363636",
            borderRadius: "50%",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <div style={{ position: "relative", width: cellPx, height: cellPx }}>
            <CompositeAvatar
              config={gearEnabled ? config : { height: config.height, hatIndex: null, weapon: null, hasShield: false }}
              bodyCol={0}
              direction="right"
              scale={previewScale}
            />
          </div>
        </div>

        {/* Attribute controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <AttributeRow
            label="Height"
            value={heightLabel(config.height)}
            onPrev={() => update({ height: cycle(HEIGHTS, config.height, -1) })}
            onNext={() => update({ height: cycle(HEIGHTS, config.height, 1) })}
          />
          {launchV1 && (
            <>
              <AttributeRow
                label="Hat"
                value={config.hatIndex !== null ? HAT_LABELS[String(config.hatIndex)] ?? "?" : "No Hat"}
                onPrev={() => update({ hatIndex: cycle(HAT_OPTIONS, config.hatIndex, -1) })}
                onNext={() => update({ hatIndex: cycle(HAT_OPTIONS, config.hatIndex, 1) })}
                disabled={!gearEnabled}
              />
              <AttributeRow
                label="Weapon"
                value={weaponLabel(config.weapon)}
                onPrev={() => update({ weapon: cycle(WEAPONS, config.weapon, -1) })}
                onNext={() => update({ weapon: cycle(WEAPONS, config.weapon, 1) })}
                disabled={!gearEnabled}
              />
              <AttributeRow
                label="Shield"
                value={config.height === "short" ? "—" : config.hasShield ? "Yes" : "No"}
                onPrev={() => update({ hasShield: !config.hasShield })}
                onNext={() => update({ hasShield: !config.hasShield })}
                disabled={!gearEnabled || config.height === "short"}
              />
            </>
          )}
        </div>
      </div>

      {launchV1 && !gearEnabled && (
        <div className="message is-success" style={{ marginTop: "2rem" }}>
          <div className="message-body upgrade-hint-body">
            <span className="icon" style={{ flexShrink: 0 }}>
              <i className="icon-info" />
            </span>
            <span style={{ fontSize: "0.85rem", flex: 1 }}>
              {t("avatar.supporterHint")}
            </span>
            <button
              className="button is-small is-success"
              onClick={() => {
                logUpsellSurfaceClicked("avatar_gear", "supporter");
                onNavigateAway?.();
                setRoute(pricingRouteHighlight("supporter", "avatar_gear"));
              }}
            >
              {t("avatar.supporterCta")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AttributeRow({
  label,
  value,
  onPrev,
  onNext,
  disabled = false,
}: {
  label: string;
  value: string;
  onPrev: () => void;
  onNext: () => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
      <span style={{ width: 56, fontSize: 12, color: "#999" }}>{label}</span>
      <Button size="small" className="is-ghost has-text-info" onClick={onPrev} disabled={disabled} style={{ padding: "0 4px" }}>
        <span className="icon is-small"><i className="icon-left-open" /></span>
      </Button>
      <span style={{ width: 52, textAlign: "center", fontSize: 13, opacity: disabled ? 0.4 : 1 }}>{value}</span>
      <Button size="small" className="is-ghost has-text-info" onClick={onNext} disabled={disabled} style={{ padding: "0 4px" }}>
        <span className="icon is-small"><i className="icon-right-open" /></span>
      </Button>
    </div>
  );
}
